import { useState, useMemo, useContext } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
// import Form from "react-bootstrap/Form";

import { Link, useSearchParams } from "react-router-dom";

import { 
  BsArrowUpRightCircle,
  BsPencilSquare
} from "react-icons/bs";

import {
  Row as RowType,
  ColumnDef
} from "@tanstack/react-table";

import { 
  formatDistanceToNowStrict
} from "date-fns";

import {
  useQuery,
  useLazyQuery,
  useMutation,
  NetworkStatus,
  ApolloError
} from "@apollo/client";

import { 
  Table, 
  Pagination,
  WidgetCard,
  Alert,
  RoleAccessButton
} from "@evos/components";
import Header from "components/Header";
import ChargerFilterBar from "components/ChargerFilterBar";
// import NotificationsList from "components/NotificationsList";
import BasicCommandModal from "components/BasicCommandModal";
import ChangeConfigModal from "components/ChangeConfigModal";
import ConfigBuilder from "./components/ConfigBuilder";
import PendingConfigs from "./components/PendingConfigs";

import { useWindowSize, usePaginationHandlers } from "@evos/hooks";
import { AuthContext } from "lib/context/auth";
import { isEmpty } from "lib/functions/general";
import {
  lastCommsFilterToTimestamp,
  lastCommsTimestampToFilter,
  normalizeChargerConfigValue,
} from "lib/functions/charger";

import { 
  CHARGERS, 
  GET_CHARGER_CONFIG, 
  CHANGE_CONFIGURATION,
  LIST_PENDING_CONFIGS
} from "./gql";

import { 
  ChargersConnection,
  WhereArgument, 
  ChangeConfigData,
  zodGetConfigFormArguments,
  ConfigBuilderFormArguments
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

interface TableData  {
  pinned?: boolean,
  serial: string,
  lastWireguardHeartbeat: string,
  allowedIps: string,
  location: string,
  mode: string,
  salesforceStatus: string,
  ocppStatus: string
  ocppServer: string,
}

type ChargerFilters = {
  ocpp_status: string[]
  installation: string[]
  last_comms: string[]
  search: string[]
}

function constructWhereArgument(filters: ChargerFilters): WhereArgument["where"] {
  return Object.values(filters).every((filter) => filter.length === 0) 
    ? null
    : {
      ...(filters.ocpp_status.length > 0 && { ocpp_status: {in: filters.ocpp_status} }),
      ...(filters.installation.length === 1
        && { installation_ref: filters.installation[0] === "Installed"
          ? {ne: null}
          : {eq: null}
        }),
      ...(filters?.last_comms?.length ?? 0) > 0
        && { last_comms: filters.last_comms[0]?.includes("<")
          ? {gte: filters.last_comms[0]?.substring(1)}
          : {lte: filters.last_comms[0]?.substring(1)},
        },
      ...(filters?.search?.[0]?.length > 0
        && { _or: [
          {
            serial: {like: filters.search[0]}
          },
          {
            firmware_version: {like: filters.search[0]}
          },
          {
            street_address: {like: filters.search[0]}
          },
          {
            suburb: {like: filters.search[0]}
          },
          {
            city: {like: filters.search[0]}
          },
          {
            province: {like: filters.search[0]}
          },
          {
            country: {like: filters.search[0]}
          }
        ]  
        }
      )
    };
}

export default function ConfigManager() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const windowWidth = useWindowSize().width ?? window.innerWidth;
  const userRoles = useMemo(() => auth?.user?.roles ?? [], [auth?.user?.roles]);

  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ serialSelected, setSerialSelected ] = useState<string | null>(null);

  const [ showGetConfigModal, setShowGetConfigModal ] = useState(false);
  const [ getConfigResult, setGetConfigResult ] = useState<ChargersConnection | null>(null);

  const [ showChangeConfigModal, setShowChangeConfigModal ] = useState(false);
  const [ changeConfigModalConfigState, setChangeConfigModalConfigState ] = useState<Record<string, string>>({});

  type CommandFeedback = { configItem: string; message?: string };
  type BuilderCommandFeedback = { serials: string[]; configItem: string; message?: string };

  const [ configModalCommandErrors, setConfigModalCommandErrors ] = useState<CommandFeedback[]>([]);
  const [ configModalCommandPending, setConfigModalCommandPending ] = useState<CommandFeedback[]>([]);
  const [ configModalCommandSuccess, setConfigModalCommandSuccess ] = useState<CommandFeedback[]>([]);

  const [ configBuilderCommandErrors, setConfigBuilderCommandErrors ] = useState<BuilderCommandFeedback[]>([]);
  const [ configBuilderCommandsPending, setConfigBuilderCommandsPending ] = useState<BuilderCommandFeedback[]>([]);
  const [ configBuilderCommandSuccess, setConfigBuilderCommandSuccess ] = useState<BuilderCommandFeedback[]>([]);

  const addModalError = (configItem: string, message: string) => {
    setConfigModalCommandPending((prevPending) => prevPending.filter((pending) => pending.configItem !== configItem));
    setConfigModalCommandSuccess((prevSuccess) => prevSuccess.filter((success) => success.configItem !== configItem));
    setConfigModalCommandErrors((prevErrors) => {
      const filtered = prevErrors.filter((error) => error.configItem !== configItem);
      return [...filtered, { configItem, message }];
    });
  };

  const addBuilderError = (configItem: string, serial: string, message: string) => {
    setConfigBuilderCommandsPending((prevPending) => prevPending.map((pending) => {
      if (pending.configItem !== configItem) {
        return pending;
      }
      return { ...pending, serials: pending.serials.filter((s) => s !== serial) };
    }).filter((pending) => pending.serials.length > 0));

    setConfigBuilderCommandSuccess((prevSuccess) => prevSuccess.map((success) => {
      if (success.configItem !== configItem) {
        return success;
      }
      return { ...success, serials: success.serials.filter((s) => s !== serial) };
    }).filter((success) => success.serials.length > 0));

    setConfigBuilderCommandErrors((prevErrors) => {
      const existing = prevErrors.find((error) => error.configItem === configItem);
      if (existing != null) {
        const nextSerials = existing.serials.includes(serial) ? existing.serials : [...existing.serials, serial];
        return prevErrors.map((error) => error.configItem === configItem ? { ...error, serials: nextSerials, message } : error);
      }
      return [...prevErrors, { serials: [serial], configItem, message }];
    });
  };

  const handleSetCurrentConfigState = async (serial?: string) => {
    if (serial != null) {
      await getConfig({
        variables: { n: 1, where: {serial: {eq: serialSelected}}},
        onCompleted: (data) => {
          // Saves current config to state when modal is opened
          // Object shape matches the shape of the form data
          const configKeyData: Record<string, string> = data?.chargers?.edges?.[0]?.node?.charger_configuration != null 
            ? Object.assign({}, ...data.chargers.edges[0].node.charger_configuration.filter((value) => value.readonly === false).map((value) => ({
              [value.config_key]: normalizeChargerConfigValue(value.config_key, value?.charger_value ?? "")
            })))
            : {};
          setChangeConfigModalConfigState(configKeyData);
        }
      });
    }
  };

  const handleChangeConfigModalShow = (show: boolean) => {
    if (!show) {
      setChangeConfigModalConfigState({});
      if (configModalCommandErrors.length > 0) {
        setConfigModalCommandErrors([]);
      }
      if (configModalCommandPending.length > 0) {
        setConfigModalCommandPending([]);
      }
      if (configModalCommandSuccess.length > 0) {
        setConfigModalCommandSuccess([]);
      }
    }
    setShowChangeConfigModal(show);
  };

  const filterSearchParams = {
    ocpp_status: searchParams.get("ocpp_status")?.split(", ") ?? [],
    installation: searchParams.get("installation")?.split(", ") ?? [],
    last_comms: searchParams.get("last_comms")?.split(", ") ?? [],
    search: searchParams.get("search")?.split(", ") ?? []
  };

  const chargersWhereArgument = constructWhereArgument(filterSearchParams);

  const { 
    data: chargersData, 
    networkStatus: chargersQueryNetworkStatus,
    error: chargersError, 
    fetchMore: fetchMoreChargers
  } = useQuery<ChargersConnection>(
    CHARGERS,
    { 
      variables: {
        chargers_n: 16, 
        after: null,
        errors_n: 1,
        chargers_where: isEmpty(chargersWhereArgument) ? null : chargersWhereArgument
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network"
    }
  );

  const {
    data: pendingConfigsQueryData,
    loading: pendingConfigsLoading,
    error: pendingConfigsError,
    refetch: refetchPendingConfigs
  } = useQuery<{chargers_config_pending: ChargersConnection["chargers"]}>(
    LIST_PENDING_CONFIGS,
    {
      variables: {
        n: 100,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network"
    }
  );

  const [getConfig, { 
    loading: getConfigLoading, 
    error: getConfigError,
  }] = useLazyQuery<ChargersConnection>(GET_CHARGER_CONFIG, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setGetConfigResult(data);
    }
  });

  const [changeConfig, { 
    loading: changeConfigLoading, 
  }] = useMutation<ChangeConfigData>(CHANGE_CONFIGURATION, {
    variables: { serial: serialSelected},
    notifyOnNetworkStatusChange: true
  });

  const chargersInitialLoading = chargersQueryNetworkStatus === NetworkStatus.loading;
  const chargersVariableChangeLoading = chargersQueryNetworkStatus === NetworkStatus.setVariables;
  const chargersFetchMoreLoading = chargersQueryNetworkStatus === NetworkStatus.fetchMore;

  const chargersPageInfo = chargersData?.chargers?.pageInfo;
  const totalCount = chargersData?.chargers?.totalCount;

  const pendingConfigsData = pendingConfigsQueryData?.chargers_config_pending?.edges?.map(c => c.node.charger_configuration);

  const { 
    paginationState,
    handleNextPage,
    handlePrevPage,
    handlePageSize,
    setPaginationState,
  } = usePaginationHandlers({
    pageInfoEndCursor: chargersPageInfo?.endCursor,
    listOfCursors: chargersData?.chargers?.edges?.map((c) => c.cursor),
    fetchMore: fetchMoreChargers,
    refetchKeyNames: ["chargers_n", "after", "chargers_where"],
    initialWhereArgument: chargersWhereArgument,
    initialShowCount: 16
  });

  const paginatedChargers = useMemo(() => (
    (chargersData?.chargers?.edges?.map((edge) => edge.node).slice(
      (paginationState.pageSize * paginationState.pageNumber - paginationState.pageSize), (paginationState.pageSize * paginationState.pageNumber)))
  ), [chargersData?.chargers?.edges, paginationState.pageNumber, paginationState.pageSize]);

  const chargerTableData = paginatedChargers?.map((d) => ({
    serial: d?.serial,
    lastComms: (d?.salesforce_details?.last_comms != null) 
      ? `${formatDistanceToNowStrict(new Date(d?.salesforce_details?.last_comms))} ago`
      : "Never",
    allowedIps: (d?.salesforce_details as { vpn_ip?: string } | undefined)?.vpn_ip != null &&
      (d?.salesforce_details as { vpn_ip?: string }).vpn_ip !== ""
      ? (d?.salesforce_details as { vpn_ip?: string }).vpn_ip
      : (d?.vpn_status?.connections[0]?.allowed_ips ?? "None"),
    ocppServer: d?.salesforce_details?.ocpp_server ?? "Unknown",
    firmwareVersion: d?.salesforce_details?.firmware_version ?? "Unknown",
  }));

  const chargerColumns = useMemo<Array<ColumnDef<TableData>>>(
    () => [
      {
        header: "Serial",
        accessorKey: "serial",
        cell: cell => <Link to={`/c/${cell.row.original.serial}`} style={{color: darkMode ? "#1E90FF" : "blue"}}>{cell.row.original.serial}</Link>
      },
      {
        header: "Last Comms",
        accessorKey: "lastComms"
      },
      {
        header: "IP Address",
        accessorKey: "allowedIps",
        cell: cell => cell.row.original.allowedIps?.includes("/")
          ? cell.row.original.allowedIps?.split("/")?.[0]
          : cell.row.original.allowedIps
      },
      {
        header: "OCPP Server",
        accessorKey: "ocppServer",
      },
      {
        header: "Firmware Version",
        accessorKey: "firmwareVersion"
      },
      {
        header: "Config Actions",
        id: "configActions",
        accessorFn: () => "configActions",
        cell: ({row}) => (
          <div className="d-flex gap-2">
            <RoleAccessButton
              userRoles={userRoles}
              expectedRole="EvosChargerEditor"
              variant="outline-secondary"
              tooltipId="view-config"
              onClick={() => {
                setSerialSelected(row.original.serial);
                setShowGetConfigModal(true);
              }}
            >
              <BsArrowUpRightCircle /> View
            </RoleAccessButton>
            <RoleAccessButton
              userRoles={userRoles}
              expectedRole="EvosChargerEditor"  
              variant="outline-secondary"
              tooltipId="edit-config"
              onClick={() => {
                setSerialSelected(row.original.serial);
                setShowChangeConfigModal(true);
              }}
            >
              <BsPencilSquare /> Edit
            </RoleAccessButton>
          </div>
        )
      }
    ],
    [darkMode, userRoles]
  );

  const getRowId = (row: TableData, relativeIndex: number, parent: RowType<TableData> | undefined) => {
    return (parent != null) ? [parent.id, row.serial].join(".") : row.serial;
  };

  const decodeQueryParams = <T,>(fieldNames: string[], initialObj: T) => {
    return fieldNames.reduce((acc, key) => 
      ({...acc, [key]: key === "search" 
        ? [searchParams.get(key) ?? ""]
        : key === "last_comms" 
          ? searchParams.get(key) != null
            ? [lastCommsTimestampToFilter(searchParams.get(key) as string)]
            : []
          : searchParams.get(key)?.split(", ") ?? []
      }), initialObj);
  };

  const setFilters = (filters: ChargerFilters) => {
    // No refetch required, setting the search params causes the chargers query
    // to re-render due to the 'cache-and-network' fetch policy
    const whereArgument = constructWhereArgument(filters);

    if (whereArgument == null || isEmpty(whereArgument)) {
      setPaginationState((prev) => ({
        ...prev, 
        where: null
      }));
      
      setSearchParams();
      
    } else {
      const encodedFilters = { 
        ocpp_status: filters.ocpp_status.join(", "),
        installation: filters.installation.join(", "),
        last_comms: lastCommsFilterToTimestamp(filters.last_comms[0]),
        search: filters.search[0]
      };
      setPaginationState((prev) => ({
        ...prev, 
        where: whereArgument
      }));

      setSearchParams((prevParams) => {
        Object.entries(encodedFilters).forEach(([key, value]) => {
          if (value.length > 0) {
            prevParams.set(key, value);
          } else {
            prevParams.delete(key);
          }
        });
        return prevParams;
      });
    }
  };

  const configBuilderChangeConfigOnError = (e: ApolloError, configItem: {name: string, set_value: string}, serial: string) => {
    if (e.message?.includes("Charger response not available")) {
      setConfigBuilderCommandsPending((prevPending) => {
        const existingPending = prevPending.find((p) => p.configItem === configItem.name);
        if (existingPending != null) {
          return prevPending.map((p) => p.configItem === configItem.name ? {serials: [...p.serials, serial], configItem: configItem.name} : p);
        } else {
          return [...prevPending, {serials: [serial], configItem: configItem.name}];
        }
      });
    } else {
      addBuilderError(configItem.name, serial, e.message ?? "Command failed");
    }
  };

  const configBuilderChangeConfigOnSuccess = (configItem: {name: string, set_value: string}, serial: string) => {
    setConfigBuilderCommandsPending((prevPending) => prevPending.map((pending) => {
      if (pending.configItem !== configItem.name) {
        return pending;
      }
      return { ...pending, serials: pending.serials.filter((s) => s !== serial) };
    }).filter((pending) => pending.serials.length > 0));

    setConfigBuilderCommandErrors((prevErrors) => {
      const existingError = prevErrors.find((error) => error.configItem === configItem.name);
      if (existingError == null) {
        return prevErrors;
      }
      const remainingSerials = existingError.serials.filter((s) => s !== serial);
      if (remainingSerials.length === 0) {
        return prevErrors.filter((error) => error.configItem !== configItem.name);
      }
      return prevErrors.map((error) => error.configItem === configItem.name ? { ...error, serials: remainingSerials } : error);
    });

    setConfigBuilderCommandSuccess((prevSuccess) => {
      const existingSuccess = prevSuccess.find((s) => s.configItem === configItem.name);
      if (existingSuccess != null) {
        return prevSuccess.map((s) => s.configItem === configItem.name ? {serials: [...s.serials, serial], configItem: configItem.name} : s);
      } else {
        return [...prevSuccess, {serials: [serial], configItem: configItem.name}];
      }
    });
  };

  const onConfigBuilderFormSubmit = async (data: ConfigBuilderFormArguments) => {
    if (data.serialSelectionType === "multiSelect") {
      for (const serial of data.serials) {
        for (const configItem of data.configItems) {
          const trimmedValue = configItem.set_value.trim();

          if (trimmedValue.length === 0) {
            addBuilderError(configItem.name, serial, "Value is required");
            continue;
          }

          if (trimmedValue.length > 500) {
            addBuilderError(configItem.name, serial, "Value must be between 1 and 500 characters");
            continue;
          }

          try {
            await changeConfig({
              variables: { 
                payload: { key: configItem.name, value: trimmedValue }, 
                serial: serial
              },
              onError: (e) => configBuilderChangeConfigOnError(e, configItem, serial),
              onCompleted: () => configBuilderChangeConfigOnSuccess(configItem, serial),
            });
          }
          catch (e) {
            console.error(e);
            return;
          }
        }
      }
      await refetchPendingConfigs();
    } else {
      const differenceInSerials = parseInt(data.serials[1]) - parseInt(data.serials[0]);
      for (let i = 0; i <= differenceInSerials; i++) {
        const serial = (parseInt(data.serials[0]) + i).toString().padStart(6, "0");
        for (const configItem of data.configItems) {
          const trimmedValue = configItem.set_value.trim();

          if (trimmedValue.length === 0) {
            addBuilderError(configItem.name, serial, "Value is required");
            continue;
          }

          if (trimmedValue.length > 500) {
            addBuilderError(configItem.name, serial, "Value must be between 1 and 500 characters");
            continue;
          }

          try {
            await changeConfig({
              variables: { 
                payload: { key: configItem.name, value: trimmedValue }, 
                serial: serial
              },
              onError: (e) => configBuilderChangeConfigOnError(e, configItem, serial),
              onCompleted: () => configBuilderChangeConfigOnSuccess(configItem, serial),
            });
          }
          catch (e) {
            console.error(e);
            return;
          }
        }
      }
      await refetchPendingConfigs();
    }
  };

  const onConfigModalFormSubmit = async (data: Record<string, string>) => {
    const changedValues = Object.entries(data).filter(([key, value]) => value !== changeConfigModalConfigState[key]);
    for (const [key, value] of changedValues) {
      const trimmedValue = (value ?? "").toString().trim();
      const trimmedCurrentValue = (changeConfigModalConfigState[key] ?? "").toString().trim();

      if (trimmedValue === trimmedCurrentValue) {
        continue;
      }

      if (trimmedValue.length === 0) {
        addModalError(key, "Value is required");
        continue;
      }

      if (trimmedValue.length > 500) {
        addModalError(key, "Value must be between 1 and 500 characters");
        continue;
      }

      try {
        await changeConfig({
          variables: { 
            payload: { key: key, value: trimmedValue}, 
            serial: serialSelected
          },
          onError: (e) => {
            if (e.message?.includes("Charger response not available")) {
              setConfigModalCommandPending((prevPending) => {
                const existingPending = prevPending.find((p) => p.configItem === key);
                if (existingPending != null) {
                  return prevPending.map((p) => p.configItem === key ? {configItem: key} : p);
                } else {
                  return [...prevPending, {configItem: key}];
                }
              });
            } else {
              setConfigModalCommandPending((prevPending) => prevPending.filter((pending) => pending.configItem !== key));
              setConfigModalCommandErrors((prevErrors) => {
                const existingError = prevErrors.find((e) => e.configItem === key);
                if (existingError != null) {
                  return prevErrors.map((e) => e.configItem === key ? { configItem: key, message: e.message ?? existingError.message } : e);
                } else {
                  return [...prevErrors, { configItem: key, message: e.message }];
                }
              });
            }
          },
          onCompleted: () => {
            setConfigModalCommandErrors((prevErrors) => prevErrors.filter((error) => error.configItem !== key));
            setConfigModalCommandSuccess((prevSuccess) => {
              const existingSuccess = prevSuccess.find((s) => s.configItem === key);
              if (existingSuccess != null) {
                return prevSuccess.map((s) => s.configItem === key ? {configItem: key} : s);
              } else {
                return [...prevSuccess, {configItem: key}];
              }
            });
          },
        });
      }
      catch (e) {
        console.error(e);
        return;
      }
    }
    await refetchPendingConfigs();
  };
  
  return (
    <div id="ConfigManager">
      <Header />
      <Container>
        <Row className="mt-3 pb-4">
          <Col xs={12}>
            <ChargerFilterBar
              darkMode={darkMode}
              windowWidth={windowWidth}
              loading={chargersInitialLoading || chargersVariableChangeLoading || chargersFetchMoreLoading}
              decodedFilters={decodeQueryParams<ChargerFilters>(
                ["ocpp_status", "installation", "last_comms", "search"], 
                {ocpp_status: [], installation: [], last_comms: [], search: []}
              )}
              setFilters={setFilters}
            />
          </Col>
          <Col xs={12}>
            <Card className={`pb-3 mt-3 ${darkMode ? "bg-dark" : ""}`}>
              <div className="mt-3 mx-3">
              </div>
              <Table
                className="mt-0"
                error={chargersError}
                isLoading={chargersInitialLoading}
                columns={chargerColumns}
                darkMode={darkMode!}
                data={chargerTableData ?? []}
                noDataText="You have no installed chargers"
                pageInfo={chargersPageInfo}
                totalCount={chargersData?.chargers?.totalCount}
                breakpointResponsiveAt="xl"
                getRowId={getRowId}
              />
              <div className="px-3">
                <Pagination
                  pageLoading={chargersFetchMoreLoading}
                  paginationState={paginationState}
                  handleNextPage={handleNextPage}
                  handlePrevPage={handlePrevPage}
                  handlePageSize={handlePageSize}
                  hasNextPage={chargersPageInfo?.hasNextPage}
                  totalCount={totalCount}
                /> 
              </div>
            </Card>
          </Col>
        </Row>
        <Row className="pb-5">
          <Col xs={12} md={8}>
            <WidgetCard 
              darkMode={darkMode}
              titleText="Config Builder"
              style={{height: "100%"}}
            >
              <ConfigBuilder 
                userRoles={userRoles}
                onConfigBuilderFormSubmit={onConfigBuilderFormSubmit}
                submitErrors={configBuilderCommandErrors}
                submitSuccess={configBuilderCommandSuccess}
                submitPending={configBuilderCommandsPending}
                submitLoading={changeConfigLoading}
                resetSubmitStatuses={() => {
                  setConfigBuilderCommandErrors([]);
                  setConfigBuilderCommandSuccess([]);
                  setConfigBuilderCommandsPending([]);
                }}
              />
            </WidgetCard>
          </Col>
          <Col xs={12} md={4}>
            <WidgetCard 
              darkMode={darkMode}
              titleText="Pending Config Items"
              style={{minHeight: "100%", maxHeight: "480px", overflow: "auto", marginBottom: 0}}
              cardBodyClassname={`${pendingConfigsError != null 
                ? "d-flex align-items-center justify-content-center" 
                : "p-0"}
              `}
            > 
              { pendingConfigsError == null
                ? <PendingConfigs 
                  loading={pendingConfigsLoading}
                  pendingConfigsData={pendingConfigsData}
                />
                : <Row>
                  <Col className="d-flex align-items-center justify-content-center">
                    <Alert variant="danger" showIcon message="There was an error getting charger error data. Please try again" />
                  </Col>
                </Row>
              }
            </WidgetCard>
          </Col>
        </Row>
      </Container>
      <BasicCommandModal
        showModal={showGetConfigModal}
        setShowModal={setShowGetConfigModal}
        header="Get Configuration"
        commandResponse={getConfigResult}
        resetMutation={() => setGetConfigResult(null)}
        isResponseLoading={getConfigLoading}
        responseError={getConfigError}
        formSubmitFn={() => getConfig({variables: { n: 1, where: {serial: {eq: serialSelected}}}})}
        zodArgumentResolver={zodGetConfigFormArguments}
      />
      <ChangeConfigModal
        showChangeConfigModal={showChangeConfigModal}
        setShowChangeConfigModal={handleChangeConfigModalShow}
        serialSelected={serialSelected ?? ""}
        currentConfigState={changeConfigModalConfigState}
        getConfigStatus={getConfigLoading
          ? "loading"
          : getConfigError != null
            ? "error"
            : getConfigResult != null
              ? "success"
              : "idle"
        }
        submitErrors={configModalCommandErrors}
        submitSuccess={configModalCommandSuccess}
        submitPending={configModalCommandPending}
        submitLoading={changeConfigLoading}
        handleSetCurrentConfigState={handleSetCurrentConfigState}
        onConfigFormSubmit={onConfigModalFormSubmit}
      />
    </div>
  );
}
